import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, KeyRound, Loader2, LogOut, ShieldCheck } from "lucide-react";
import { AdesioneDati } from "./AdesioneDati";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/clienti`;
const ax = axios.create({ baseURL: API, withCredentials: true });
const msg = (e, d = "Errore") => { const x = e?.response?.data?.detail; return typeof x === "string" ? x : Array.isArray(x) ? x.map((y) => y.msg).join(" ") : d; };
const origin = () => window.location.origin;

function Shell({ titolo, children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-4"><div className="text-[10px] tracking-widest text-teal-700 font-semibold">ASSIETICASUITE · ADESIONE ONLINE</div><div className="text-xs text-slate-500">Intermediario: Assietica</div></div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm"><h1 className="text-xl font-bold text-slate-900" data-testid="ad-titolo">{titolo}</h1>{children}</div>
      </div>
    </div>
  );
}
const Campo = ({ l, ...p }) => <label className="block"><span className="text-xs text-slate-500">{l}</span><input {...p} className="mt-0.5 w-full h-11 border border-slate-300 rounded-lg px-3 text-sm" /></label>;
const Bottone = ({ busy, children, ...p }) => <button {...p} disabled={busy || p.disabled} className="w-full h-11 rounded-xl bg-teal-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{children}</button>;
const Errore = ({ e }) => (e ? <p className="text-sm text-red-600" data-testid="ad-errore">{e}</p> : null);
const REGOLE = "Almeno 10 caratteri, con maiuscole, minuscole e numeri.";

export function AdesioneInvito() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [f, setF] = useState({ password: "", ripeti: "", privacy: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [fatto, setFatto] = useState(false);
  useEffect(() => { ax.get(`/invito/${token}`).then(({ data }) => setInv(data)).catch((e) => setErr(msg(e, "Invito non valido"))); }, [token]);
  const invia = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password !== f.ripeti) { setErr("Le due password non coincidono"); return; }
    setBusy(true);
    try { await ax.post("/registrazione", { token, password: f.password, privacy: f.privacy, origin: origin() }); setFatto(true); } catch (er) { setErr(msg(er)); } finally { setBusy(false); }
  };
  if (fatto) return <Shell titolo="Controlli la Sua email"><p className="text-sm text-slate-600" data-testid="ad-reg-ok">Le abbiamo inviato un link per confermare l'indirizzo <b>{inv.email}</b>. Dopo la conferma potrà accedere con email e password.</p></Shell>;
  if (!inv) return <Shell titolo="Invito all'adesione online">{err ? <><Errore e={err} /><Link to="/adesione/accesso" className="text-sm text-teal-700 underline">Vai all'accesso</Link></> : <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}</Shell>;
  return (
    <Shell titolo="Crei il Suo accesso">
      <p className="text-sm text-slate-600">Gentile {inv.nome || "Cliente"}, imposti la password per l'area riservata.</p>
      <form onSubmit={invia} className="space-y-3">
        <Campo l="Email" value={inv.email} readOnly data-testid="ad-reg-email" />
        <Campo l="Password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="ad-reg-password" autoComplete="new-password" />
        <Campo l="Ripeti la password" type="password" value={f.ripeti} onChange={(e) => setF({ ...f, ripeti: e.target.value })} data-testid="ad-reg-ripeti" autoComplete="new-password" />
        <p className="text-[11px] text-slate-500">{REGOLE}</p>
        <label className="flex gap-2 text-xs text-slate-600"><input type="checkbox" checked={f.privacy} onChange={(e) => setF({ ...f, privacy: e.target.checked })} data-testid="ad-reg-privacy" />
          Ho preso visione dell'informativa privacy (v. {inv.privacy_versione}) e delle condizioni d'uso dell'area riservata.</label>
        <Errore e={err} />
        <Bottone busy={busy} disabled={!f.privacy || !f.password} data-testid="ad-reg-invia">Crea l'accesso</Bottone>
      </form>
    </Shell>
  );
}

export function AdesioneConferma() {
  const { token } = useParams();
  const [st, setSt] = useState({ ok: null, err: "" });
  const fatto = useRef(false);
  useEffect(() => { if (fatto.current) return; fatto.current = true; ax.post("/conferma", { token }).then(() => setSt({ ok: true })).catch((e) => setSt({ ok: false, err: msg(e) })); }, [token]);
  return (
    <Shell titolo="Conferma dell'email">
      {st.ok === null && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
      {st.ok && <p className="text-sm text-emerald-700 flex gap-2" data-testid="ad-conf-ok"><CheckCircle2 className="h-5 w-5" /> Indirizzo confermato. Ora può accedere.</p>}
      {st.ok === false && <Errore e={st.err} />}
      <Link to="/adesione/accesso" className="block text-center text-sm text-teal-700 underline" data-testid="ad-vai-accesso">Vai all'accesso</Link>
    </Shell>
  );
}

export function AdesioneAccesso() {
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", codice: "" });
  const [cod, setCod] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const entra = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const { data } = await ax.post("/login", f); if (data.codice_richiesto) { setCod(true); setInfo("Le abbiamo inviato un codice di accesso via email."); } else nav("/adesione/area"); }
    catch (er) { setErr(msg(er)); } finally { setBusy(false); }
  };
  const reinvia = async () => { try { await ax.post("/reinvia-conferma", { email: f.email, origin: origin() }); setErr(""); setInfo("Se l'account esiste e non è confermato, abbiamo inviato un nuovo link."); } catch (er) { setErr(msg(er)); } };
  return (
    <Shell titolo="Accesso all'area riservata">
      <form onSubmit={entra} className="space-y-3">
        <Campo l="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="ad-login-email" autoComplete="username" />
        <Campo l="Password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="ad-login-password" autoComplete="current-password" />
        {cod && <Campo l="Codice ricevuto via email" inputMode="numeric" maxLength={6} value={f.codice} onChange={(e) => setF({ ...f, codice: e.target.value.replace(/\D/g, "") })} data-testid="ad-login-codice" />}
        {info && <p className="text-xs text-teal-800" data-testid="ad-info">{info}</p>}
        <Errore e={err} />
        <Bottone busy={busy} disabled={!f.email || !f.password} data-testid="ad-login-invia"><KeyRound className="h-4 w-4" /> Accedi</Bottone>
      </form>
      <div className="flex justify-between text-xs">
        <Link to="/adesione/password" className="text-teal-700 underline" data-testid="ad-link-password">Password dimenticata?</Link>
        {err.startsWith("Confermi") && <button onClick={reinvia} className="text-teal-700 underline" data-testid="ad-reinvia">Invia di nuovo il link di conferma</button>}
      </div>
    </Shell>
  );
}

export function AdesionePassword() {
  const [email, setEmail] = useState("");
  const [ok, setOk] = useState(false);
  const invia = async (e) => { e.preventDefault(); try { await ax.post("/password-dimenticata", { email, origin: origin() }); } catch { /* risposta sempre neutra */ } setOk(true); };
  return (
    <Shell titolo="Password dimenticata">
      {ok ? <p className="text-sm text-slate-600" data-testid="ad-pw-ok">Se l'indirizzo è registrato, riceverà un'email con il link per reimpostare la password (valido 1 ora).</p> : (
        <form onSubmit={invia} className="space-y-3"><Campo l="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="ad-pw-email" />
          <Bottone disabled={!email} data-testid="ad-pw-invia">Invia il link</Bottone></form>)}
      <Link to="/adesione/accesso" className="block text-center text-xs text-teal-700 underline">Torna all'accesso</Link>
    </Shell>
  );
}

export function AdesioneReset() {
  const { token } = useParams();
  const [f, setF] = useState({ password: "", ripeti: "" });
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const invia = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password !== f.ripeti) { setErr("Le due password non coincidono"); return; }
    try { await ax.post("/reset-password", { token, password: f.password }); setOk(true); } catch (er) { setErr(msg(er)); }
  };
  return (
    <Shell titolo="Nuova password">
      {ok ? <p className="text-sm text-emerald-700" data-testid="ad-reset-ok">Password aggiornata. Per sicurezza le sessioni aperte sono state chiuse.</p> : (
        <form onSubmit={invia} className="space-y-3">
          <Campo l="Nuova password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="ad-reset-password" autoComplete="new-password" />
          <Campo l="Ripeti la password" type="password" value={f.ripeti} onChange={(e) => setF({ ...f, ripeti: e.target.value })} data-testid="ad-reset-ripeti" autoComplete="new-password" />
          <p className="text-[11px] text-slate-500">{REGOLE}</p><Errore e={err} /><Bottone disabled={!f.password} data-testid="ad-reset-invia">Salva la password</Bottone></form>)}
      <Link to="/adesione/accesso" className="block text-center text-xs text-teal-700 underline">Vai all'accesso</Link>
    </Shell>
  );
}

export function AdesioneArea() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const carica = useCallback(async () => {
    try { setMe((await ax.get("/me")).data); }
    catch { try { await ax.post("/refresh"); setMe((await ax.get("/me")).data); } catch { nav("/adesione/accesso"); } }
  }, [nav]);
  useEffect(() => { carica(); const t = setInterval(() => ax.post("/refresh").catch(() => nav("/adesione/accesso")), 10 * 60 * 1000); return () => clearInterval(t); }, [carica, nav]);
  const esci = async () => { await ax.post("/logout").catch(() => {}); nav("/adesione/accesso"); };
  const sf = async () => { try { const { data } = await ax.put("/me/secondo-fattore", { attivo: !me.secondo_fattore }); setMe(data); } catch { nav("/adesione/accesso"); } };
  if (!me) return <Shell titolo="Area riservata"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></Shell>;
  return (
    <Shell titolo={`Benvenuto${me.nome ? ", " + me.nome : ""}`}>
      <p className="text-sm text-slate-600" data-testid="ad-area-email">Accesso eseguito come <b>{me.email}</b>.</p>
      <div className="border-t pt-4"><AdesioneDati /></div>
      <label className="flex items-center justify-between gap-3 text-sm border rounded-xl p-3"><span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-teal-700" /> Codice di accesso via email (secondo fattore)</span>
        <input type="checkbox" checked={me.secondo_fattore} onChange={sf} data-testid="ad-area-2fa" /></label>
      <button onClick={esci} className="w-full h-10 rounded-xl border text-sm inline-flex items-center justify-center gap-2" data-testid="ad-area-esci"><LogOut className="h-4 w-4" /> Esci</button>
    </Shell>
  );
}
